import { useEffect, useCallback, useLayoutEffect, useRef, useState } from "react";
import Button from "@/components/Button";
import DateSelect from "@/components/DateSelect";
import Dialog from "@/components/Dialog";
import TextField from "@/components/TextField";
import { Controller, useForm } from "react-hook-form";
import { DateRangeType } from "react-tailwindcss-datepicker";
import { InteractionInput } from "../types";

const MIN_DESCRIPTION_ROWS = 4;
const DEFAULT_DESCRIPTION_ROWS = 8;
const DESCRIPTION_LABEL_RESERVE_PX = 16;
const TEXT_FIELD_CHROME_PX = 32;

export function getDescriptionRows(container: HTMLElement) {
  const availableHeight = container.clientHeight - DESCRIPTION_LABEL_RESERVE_PX - TEXT_FIELD_CHROME_PX;
  if (availableHeight <= 0) {
    return DEFAULT_DESCRIPTION_ROWS;
  }

  const textField = container.querySelector("md-outlined-text-field");
  const textarea = textField?.shadowRoot?.querySelector("textarea");
  const lineHeight = textarea ? Number.parseFloat(getComputedStyle(textarea).lineHeight) || 24 : 24;

  return Math.max(MIN_DESCRIPTION_ROWS, Math.floor(availableHeight / lineHeight));
}

export default function EditMessageModal({
  open,
  setOpen,
  variables,
  setVariables,
  saveInteraction,
  disabled
}: {
  open: boolean;
  setOpen: (value: boolean) => void;
  variables: InteractionInput;
  setVariables: (value: InteractionInput) => void;
  saveInteraction: () => void;
  disabled: boolean;
}) {
  const descriptionContainerRef = useRef<HTMLDivElement>(null);
  const [descriptionRows, setDescriptionRows] = useState(DEFAULT_DESCRIPTION_ROWS);

  const { control, handleSubmit, reset } = useForm({
    mode: "onChange",
    defaultValues: {
      interactionDate: { startDate: variables.interactionDate, endDate: variables.interactionDate },
      interactionDescription: variables.description || ""
    }
  });

  const updateDescriptionRows = useCallback(() => {
    const container = descriptionContainerRef.current;
    if (!container) return;

    setDescriptionRows(getDescriptionRows(container));
  }, []);

  useLayoutEffect(() => {
    if (!open) return;

    const descriptionContainer = descriptionContainerRef.current;
    if (!descriptionContainer) return;

    const contentSlot = descriptionContainer.closest('[slot="content"]') as HTMLElement | null;
    const dialog = descriptionContainer.closest("md-dialog");
    const scroller = dialog?.shadowRoot?.querySelector(".scroller") as HTMLElement | null;
    const dialogContainer = dialog?.shadowRoot?.querySelector(".container") as HTMLElement | null;
    const dialogContent = dialog?.shadowRoot?.querySelector(".content") as HTMLElement | null;

    if (contentSlot) {
      contentSlot.classList.add("flex", "h-full", "min-h-0", "flex-col", "overflow-visible");
      contentSlot.style.height = "100%";
    }
    if (dialogContainer) {
      dialogContainer.style.overflow = "visible";
    }
    if (dialogContent) {
      dialogContent.style.overflow = "visible";
    }
    if (scroller) {
      scroller.style.minHeight = "0";
      scroller.style.overflow = "visible";
    }

    updateDescriptionRows();
    const resizeObserver = new ResizeObserver(updateDescriptionRows);
    resizeObserver.observe(descriptionContainer);

    return () => {
      resizeObserver.disconnect();
      if (contentSlot) {
        contentSlot.classList.remove("flex", "h-full", "min-h-0", "flex-col", "overflow-visible");
        contentSlot.style.height = "";
      }
      if (dialogContainer) {
        dialogContainer.style.overflow = "";
      }
      if (dialogContent) {
        dialogContent.style.overflow = "";
      }
      if (scroller) {
        scroller.style.minHeight = "";
        scroller.style.overflow = "";
      }
    };
  }, [open, updateDescriptionRows]);

  useEffect(() => {
    if (open) {
      reset({
        interactionDate: { startDate: variables.interactionDate, endDate: variables.interactionDate },
        interactionDescription: variables.description || ""
      });
    }
  }, [open, reset, variables.interactionDate, variables.description]);

  const handleClose = useCallback(() => {
    setVariables({ ...variables, description: null });
    setOpen(false);
  }, [setOpen, setVariables, variables]);

  const handleDateChange = useCallback(
    (e: any, onChange: (value: any) => void) => {
      if (e) {
        onChange(e);
        const dateRange = e as DateRangeType;
        setVariables({ ...variables, interactionDate: dateRange.startDate });
      }
    },
    [setVariables, variables]
  );

  const handleDescriptionChange = useCallback(
    (text: string, onChange: (value: any) => void) => {
      onChange(text);
      setVariables({ ...variables, description: text.trim() === "" ? null : text });
    },
    [setVariables, variables]
  );

  return (
    <Dialog
      open={open}
      onClosed={handleClose}
      headline="Edit Description"
      className="edit-description-modal p-6 pr-2 w-[800px] max-w-[95vw] !max-h-[calc(100dvh-3rem)] !h-[min(85dvh,calc(100dvh-3rem))]"
      actions={
        <>
          <Button variant="text" disabled={disabled} onClick={handleClose}>
            Cancel
          </Button>

          <Button trailingIcon={false} icon="check" onClick={handleSubmit(saveInteraction)} disabled={disabled}>
            Save
          </Button>
        </>
      }
      hasFormElements
      displayOverflow
    >
      <div className="flex h-full min-h-0 flex-col overflow-visible pt-2">
        <input className="hidden"></input>
        <div className="flex min-h-0 flex-1 flex-col gap-6 overflow-visible">
          <div className="shrink-0 overflow-visible pt-2">
            <Controller
              name="interactionDate"
              control={control}
              rules={{
                validate: {
                  startDateNotNull: (value) => value?.startDate !== null || "Date cannot be null",
                  startDateNotInFuture: (value) => {
                    if (!value?.startDate) return true;
                    const date = new Date(value.startDate);
                    return date <= new Date() || "Date cannot be in the future";
                  }
                }
              }}
              render={({ field, fieldState }) => (
                <DateSelect
                  label="Date *"
                  value={field.value}
                  onChange={(e) => handleDateChange(e, field.onChange)}
                  maxDate={new Date()}
                  useSingleDate
                  useModal={true}
                  showFooter={false}
                  error={fieldState.invalid}
                  errorMessage={fieldState.error?.message}
                />
              )}
            />
          </div>

          <div ref={descriptionContainerRef} className="min-h-0 flex-1 overflow-visible pt-4">
            <Controller
              name="interactionDescription"
              control={control}
              rules={{ required: "Description is required" }}
              render={({ field: { onChange, value }, fieldState }) => (
                <TextField
                  value={value}
                  onChangeText={(text) => handleDescriptionChange(text, onChange)}
                  disabled={disabled}
                  required
                  label="Description"
                  type="textarea"
                  rows={descriptionRows}
                  className="block w-full"
                  error={fieldState.invalid}
                  errorText={fieldState.error?.message}
                />
              )}
            />
          </div>
        </div>
      </div>
    </Dialog>
  );
}
